import React, { useEffect, useState } from "react";
import { useReservas } from "../../hooks/useReservas";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useSession } from "../../context/SessionContext";
import { Reserva } from "../../types/reserva.types";
import { normalizeForCompare } from "../../lib/formatters";
import { Button, notify, Spinner } from "../ui";
import { SuggestInput } from "../patterns/SuggestInput";
import { t } from "../../i18n";

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

interface ReservaLinkFormProps {
  // Reservas já vinculadas ao médico — usadas para não sugerir de novo uma
  // reserva que ele já tem, e para bloquear duplicidade ao criar uma nova.
  existingReservas: Reserva[];
  // Disparado depois que uma reserva é vinculada com sucesso (nova ou já
  // existente).
  onLinked: (reserva: Reserva) => void;
  submitLabel?: string;
}

// Núcleo reutilizável do fluxo "buscar/cadastrar/vincular uma reserva" —
// mesmo padrão de `HospitalLinkForm`/`CompanyLinkForm`, mas com um único
// campo (`name`) e sem etapa de revisão ao selecionar uma sugestão: o
// backend já normaliza o nome (capitalização + siglas médicas), então não há
// nenhum outro dado a conferir antes de vincular.
const ReservaLinkForm: React.FC<ReservaLinkFormProps> = ({
  existingReservas,
  onLinked,
  submitLabel = t("links.reserva.submit"),
}) => {
  const { searchReservas, createReserva, linkReserva, searching, creating, linking } = useReservas();
  const { setMetadata } = useSession();

  const [nameInput, setNameInput] = useState("");
  const [suggestions, setSuggestions] = useState<Reserva[]>([]);

  const debouncedName = useDebouncedValue(nameInput, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    const query = debouncedName.trim();

    // `debouncedName` só reflete o texto atual depois que o delay termina.
    // Sem esta checagem, dispararíamos uma busca fantasma para um texto
    // que o usuário já apagou/trocou antes do debounce terminar.
    if (query !== nameInput.trim() || query.length < MIN_SEARCH_LENGTH) {
      setSuggestions([]);
      return;
    }

    let cancelled = false;
    searchReservas(query).then((result) => {
      if (!cancelled && result) setSuggestions(result);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedName]);

  const isAlreadyLinked = (reservaId: number) => existingReservas.some((r) => r.id === reservaId);

  const clearForm = () => {
    setNameInput("");
    setSuggestions([]);
  };

  const linkAndSync = async (reserva: Reserva) => {
    if (!(await linkReserva(reserva.id))) return false;

    // `metadata.reservas` é lido em outras telas do perfil do médico — sem
    // atualizar aqui, essas telas ficariam com a lista desatualizada até o
    // médico deslogar e logar de novo.
    setMetadata((prev) => ({
      ...prev,
      reservas: prev.reservas.some((r) => r.id === reserva.id) ? prev.reservas : [...prev.reservas, reserva],
    }));
    onLinked(reserva);
    return true;
  };

  // Reserva já existe no sistema — vincula direto, sem etapa de revisão (não
  // há outro campo além do nome para conferir antes de confirmar).
  const handleSelectSuggestion = async (reserva: Reserva) => {
    setSuggestions([]);
    setNameInput("");
    await linkAndSync(reserva);
  };

  const handleAdd = async () => {
    const trimmedName = nameInput.trim();

    if (!trimmedName) {
      notify({ tone: "error", title: t("links.reserva.nameRequired") });
      return;
    }

    if (existingReservas.some((r) => r.name.toLowerCase() === trimmedName.toLowerCase())) {
      notify({ tone: "error", title: t("links.reserva.alreadyLinked") });
      return;
    }

    // O backend normaliza capitalização e siglas médicas automaticamente —
    // envia o texto como digitado, sem tentar replicar essa formatação aqui.
    const created = await createReserva({ name: trimmedName });
    // erro já foi mostrado via toast pelo useApi (ex: nome vazio)
    if (!created) return;

    if (await linkAndSync(created)) clearForm();
  };

  const isSubmitting = creating || linking;

  // `suggestions` só é atualizado quando a busca debounced resolve — sem este
  // filtro, uma sugestão buscada para um texto anterior continuaria clicável
  // mesmo depois do médico já ter digitado algo que não bate mais com ela.
  const normalizedQuery = normalizeForCompare(nameInput.trim());
  const visibleSuggestions = suggestions.filter(
    (r) => !isAlreadyLinked(r.id) && normalizeForCompare(r.name).includes(normalizedQuery),
  );

  return (
    <div className="flex flex-col gap-2 w-full">
      <SuggestInput
        value={nameInput}
        onChange={setNameInput}
        onEnter={handleAdd}
        placeholder={t("links.reserva.namePlaceholder")}
        disabled={isSubmitting}
        searching={searching}
        suggestions={visibleSuggestions}
        getKey={(reserva) => reserva.id}
        onSelect={handleSelectSuggestion}
        renderSuggestion={(reserva) => <span className="font-medium">{reserva.name}</span>}
      />

      {linking && (
        <span className="flex items-center gap-1 text-caption text-fg-subtle">
          <Spinner size={11} /> {t("links.reserva.linking")}
        </span>
      )}

      <Button
        variant="primary"
        className="self-start"
        onClick={handleAdd}
        loading={isSubmitting}
        disabled={!nameInput.trim()}
      >
        {submitLabel}
      </Button>
    </div>
  );
};

export default ReservaLinkForm;
